import { InjectionToken } from '@angular/core';

/**
 * Configuration options for tab detection functionality.
 *
 * This interface is used to define the properties required to configure
 * the behavior of the tab detection system. It allows specifying the mode
 * of handling tab detection, channel identification, and optional customization.
 *
 * Properties:
 * - mode: Determines how the system reacts to multiple tabs. Can be one of the following:
 *   - 'block': Blocks opening another tab.
 *   - 'warn': Shows a warning about multiple tabs.
 *   - 'silent': Allows multiple tabs silently.
 * - channelName: A unique identifier for grouping tabs under the same channel.
 * - enableInDevelopment: Specifies whether tab detection should be enabled during development.
 * - redirectUrl: Optional URL to redirect users when a new tab is detected.
 * - customMessage: Optional custom message to display in case of tab detection.
 */
export interface TabDetectionConfig {
  mode: 'block' | 'warn' | 'silent';
  channelName: string;
  enableInDevelopment: boolean;
  redirectUrl?: string;
  customMessage?: string;
}

/**
 * Default configuration for tab detection functionality in the application.
 *
 * @variable {TabDetectionConfig} DEFAULT_TAB_DETECTION_CONFIG
 * @property {string} mode The mode of operation for tab detection. Commonly 'warn' or 'block'.
 * @property {string} channelName Name of the communication channel used to detect open tabs.
 * @property {boolean} enableInDevelopment Determines whether tab detection is enabled during development.
 * @property {string} redirectUrl URL to which the user is redirected when a duplicate tab is detected.
 * @property {string} customMessage Custom message displayed when duplicate tab detection occurs.
 */
export const DEFAULT_TAB_DETECTION_CONFIG: Partial<TabDetectionConfig> = {
  mode: 'block',
  channelName: 'app-tab-detection',
  enableInDevelopment: true, // MUST be true for development testing
  customMessage: 'This application is already running in another tab',
};

/**
 * Injection token used to configure the tab detection settings within the application.
 * The configuration provided using this token defines how tab detection is handled.
 *
 * This token is provided in the root injector and is initialized with the default
 * tab detection configuration, specified by the `DEFAULT_TAB_DETECTION_CONFIG` object.
 *
 * @constant {InjectionToken<TabDetectionConfig>} TAB_DETECTION_CONFIG_TOKEN
 */
export const TAB_DETECTION_CONFIG_TOKEN = new InjectionToken<Partial<TabDetectionConfig>>(
  'tab-detection-config',
  {
    providedIn: 'root',
    factory: () => DEFAULT_TAB_DETECTION_CONFIG,
  }
);
